/**
 * La mesa de entrada: todo lo que se está leyendo, con su avance en vivo.
 *
 * Por qué se siente rápida:
 *  1. La tarjeta aparece en el mismo fotograma en que se suelta el archivo.
 *  2. El original sube EN PARALELO a la conversión del navegador.
 *  3. Las miniaturas que rasteriza la imprenta se enseñan al instante, antes de
 *     que el servidor haya leído nada: el usuario ve sus páginas en segundos.
 *  4. Después, cada evento `unidades` del tiempo real marca páginas ya legibles.
 */
import { useSyncExternalStore } from 'react';
import { subirFichero, type EventoTiempoReal, type SubidaCreada } from '@scholaris/contrato';
import type { FaseIngesta, TipoEntrada } from '@scholaris/nucleo';
import type { ArchivoEntrada, EventoConversion, PaqueteConversion, ParteBinaria } from '@scholaris/imprenta';
import { api, esSimulado, escucharTiempoReal } from './api';
import { clienteConsultas } from './consultas';
import { anotarPagina } from './previa';
import type { MensajeDesdeImprenta, MensajeHaciaImprenta } from '../trabajadores/imprenta.worker';

export type Etapa = 'preparando' | 'convirtiendo' | 'procesando' | 'listo' | 'error' | 'cancelada' | 'duplicado';

export interface Ingesta {
  id: string;
  nombre: string;
  tipo: TipoEntrada;
  bytes: number;
  documento?: string;
  tarea?: string;
  etapa: Etapa;
  fase?: FaseIngesta;
  /** 0-1 global. */
  avance: number;
  /** 0-1 de la subida del original. */
  subido: number;
  unidades: number | null;
  /** Segundos (audio y vídeo). */
  duracion?: number;
  /** Unidades rasterizadas en el navegador. */
  preparadas: number;
  /** Unidades ya leídas por el servidor. */
  leidas: number;
  /** Unidades ya buscables (indexadas mientras se lee). */
  buscables: number;
  miniaturas: string[];
  mensaje?: string;
  error?: string;
  inicio: number;
  fin?: number;
}

// ---------------------------------------------------------------------------
// Tiempos percibidos: desde soltar hasta ver, leer y buscar.
// ---------------------------------------------------------------------------

/** miniatura: se ve la primera página · legible: se puede leer su texto · listo: se puede buscar y citar. */
export interface Hitos { miniatura?: number; legible?: number; buscable?: number; listo?: number }
const hitos = new Map<string, Hitos>();
/** Anota un hito una sola vez y lo deja en la consola y en `performance` (para medir). */
function hito(i: Ingesta, k: keyof Hitos) {
  const h = hitos.get(i.id) ?? {};
  if (h[k] != null) return;
  h[k] = Date.now() - i.inicio;
  hitos.set(i.id, h);
  try { performance.mark(`scholaris:${k}:${i.nombre}`); } catch { /* sin performance */ }
  console.info(`[ingesta] ${i.nombre}: ${k === 'miniatura' ? 'primera miniatura' : k === 'legible' ? 'página 1 legible' : k === 'buscable' ? 'primeras páginas buscables' : 'lista para buscar'} a ${(h[k]! / 1000).toFixed(1)} s`);
}
export const hitosDe = (id: string) => hitos.get(id);
if (typeof window !== 'undefined') (window as unknown as { __hitos: typeof hitos }).__hitos = hitos;

// ---------------------------------------------------------------------------
// El almacén
// ---------------------------------------------------------------------------

let estado: Ingesta[] = [];
const oyentes = new Set<() => void>();
const emitir = () => oyentes.forEach((o) => o());

function poner(id: string, cambio: Partial<Ingesta> | ((i: Ingesta) => Partial<Ingesta>)) {
  estado = estado.map((i) => (i.id === id ? { ...i, ...(typeof cambio === 'function' ? cambio(i) : cambio) } : i));
  emitir();
}

export function useIngestas(): Ingesta[] {
  return useSyncExternalStore((o) => { oyentes.add(o); return () => oyentes.delete(o); }, () => estado, () => estado);
}

export function retirarIngesta(id: string) {
  const i = estado.find((x) => x.id === id);
  i?.miniaturas.forEach((u) => u.startsWith('blob:') && URL.revokeObjectURL(u));
  estado = estado.filter((x) => x.id !== id);
  emitir();
}

const controles = new Map<string, { cancelar: () => void }>();

export async function cancelarIngesta(id: string) {
  const i = estado.find((x) => x.id === id);
  controles.get(id)?.cancelar();
  poner(id, { etapa: 'cancelada', fin: Date.now() });
  try {
    if (i?.tarea) await api().tareas.cancelar(i.tarea);
  } catch { /* ya terminó */ }
  setTimeout(() => retirarIngesta(id), 400);
  void clienteConsultas.invalidateQueries({ queryKey: ['documentos'] });
}

// ---------------------------------------------------------------------------
// Tipos de entrada
// ---------------------------------------------------------------------------

const EXT: Record<string, TipoEntrada> = {
  pdf: 'pdf', epub: 'epub', docx: 'documento', doc: 'documento', odt: 'documento', rtf: 'documento', md: 'documento', markdown: 'documento', txt: 'documento', html: 'documento', htm: 'documento',
  pptx: 'presentacion', key: 'presentacion', odp: 'presentacion', xlsx: 'hoja', xls: 'hoja', csv: 'hoja', ods: 'hoja', tsv: 'hoja',
  mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio', opus: 'audio', flac: 'audio', aac: 'audio',
  mp4: 'video', mov: 'video', webm: 'video', mkv: 'video', m4v: 'video',
  jpg: 'imagen', jpeg: 'imagen', png: 'imagen', webp: 'imagen', heic: 'imagen', heif: 'imagen', tif: 'imagen', tiff: 'imagen', gif: 'imagen',
};

export function deducirTipo(f: { name: string; type: string }): TipoEntrada {
  const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
  if (EXT[ext]) return EXT[ext]!;
  if (f.type.startsWith('image/')) return 'imagen';
  if (f.type.startsWith('audio/')) return 'audio';
  if (f.type.startsWith('video/')) return 'video';
  if (f.type === 'application/pdf') return 'pdf';
  return 'documento';
}

// ---------------------------------------------------------------------------
// La imprenta del navegador
// ---------------------------------------------------------------------------

interface Conversion {
  eventos: AsyncGenerator<EventoConversion | { tipo: 'no_disponible' }>;
  cancelar: () => void;
}

async function bytesDe(f: File): Promise<ArchivoEntrada> {
  return { nombre: f.name, mime: f.type || undefined, bytes: new Uint8Array(await f.arrayBuffer()) };
}

function convertirEnHilo(archivo: File, fotos?: File[]): Conversion {
  const hilo = new Worker(new URL('../trabajadores/imprenta.worker.ts', import.meta.url), { type: 'module', name: 'imprenta' });
  const cola: Array<MensajeDesdeImprenta> = [];
  let despertar: (() => void) | null = null;
  const TIPOS = new Set(['evento', 'no_disponible', 'fallo', 'hecho']);
  // Solo nuestros mensajes: pdf.js a veces anuncia su propio «ready».
  hilo.onmessage = (ev: MessageEvent<MensajeDesdeImprenta>) => { if (!TIPOS.has((ev.data as { tipo?: string })?.tipo ?? '')) return; cola.push(ev.data); despertar?.(); };
  hilo.onerror = (ev) => { cola.push({ tipo: 'fallo', mensaje: ev.message || 'La imprenta del navegador se detuvo' }); despertar?.(); };
  void (async () => {
    const msj: MensajeHaciaImprenta = { tipo: 'convertir', archivo: await bytesDe(archivo), ...(fotos ? { fotos: await Promise.all(fotos.map(bytesDe)) } : {}) };
    hilo.postMessage(msj, [msj.archivo.bytes.buffer as ArrayBuffer]);
  })();
  async function* eventos() {
    try {
      while (true) {
        while (!cola.length) await new Promise<void>((r) => (despertar = r));
        const m = cola.shift()!;
        if (m.tipo === 'evento') yield m.evento;
        else if (m.tipo === 'no_disponible') { yield { tipo: 'no_disponible' as const }; return; }
        else if (m.tipo === 'fallo') throw new Error(m.mensaje);
        else return;
      }
    } finally { hilo.terminate(); }
  }
  return { eventos: eventos(), cancelar: () => { hilo.postMessage({ tipo: 'cancelar' } satisfies MensajeHaciaImprenta); hilo.terminate(); } };
}

/** Qué entradas sabe convertir el navegador (lo demás lo hace el servidor). */
const CONVIERTE_NAVEGADOR = new Set<TipoEntrada>(['pdf', 'pdf_escaneado', 'fotos', 'imagen', 'audio', 'video', 'documento', 'epub', 'hoja', 'presentacion']);

// ---------------------------------------------------------------------------
// Subida de partes
// ---------------------------------------------------------------------------

async function ponerBytes(destino: SubidaCreada['original'], cuerpo: Blob) {
  if (esSimulado()) { await new Promise((r) => setTimeout(r, 20)); return; }
  if (destino.modo !== 'simple' || !destino.url) throw new Error('Recurso sin URL simple');
  const r = await fetch(destino.url, { method: 'PUT', body: cuerpo, headers: destino.cabeceras });
  if (!r.ok) throw new Error(`No se pudo subir un recurso (${r.status})`);
}

/** Sube partes en tandas: pide URLs por lotes y las sube con concurrencia limitada. */
function crearSubidorPartes(subida: string) {
  const pendientes: Array<{ parte: ParteBinaria; datos: Uint8Array }> = [];
  let enVuelo = Promise.resolve();
  let fallo: unknown = null;
  const vaciar = () => {
    if (!pendientes.length) return;
    const lote = pendientes.splice(0, 24);
    enVuelo = enVuelo.then(async () => {
      const { recursos } = await api().subidas.recursos(subida, { recursos: lote.map((x) => ({ ruta: x.parte.id, mime: x.parte.mime, bytes: x.parte.bytes })) });
      const porRuta = new Map(recursos.map((r) => [r.ruta, r.subida]));
      let i = 0;
      await Promise.all(Array.from({ length: 6 }, async () => {
        while (i < lote.length) {
          const x = lote[i++]!;
          const d = porRuta.get(x.parte.id);
          if (d) await ponerBytes(d, new Blob([x.datos as BlobPart], { type: x.parte.mime }));
        }
      }));
    }).catch((e) => { fallo = e; });
  };
  return {
    anadir(parte: ParteBinaria, datos: Uint8Array) { pendientes.push({ parte, datos }); if (pendientes.length >= 24) vaciar(); },
    async terminar() { vaciar(); await enVuelo; if (fallo) throw fallo; },
  };
}

// ---------------------------------------------------------------------------
// El flujo de una ingesta
// ---------------------------------------------------------------------------

/** «the_discarded-image.pdf» → «The discarded image»: mientras no llegue el título de verdad. */
function nombreLegible(archivo: string): string {
  const base = archivo.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : archivo;
}

function nueva(nombre: string, tipo: TipoEntrada, bytes: number): Ingesta {
  const i: Ingesta = { id: `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, nombre, tipo, bytes, etapa: 'preparando', avance: 0, subido: 0, unidades: null, preparadas: 0, leidas: 0, buscables: 0, miniaturas: [], inicio: Date.now(), mensaje: 'Preparando…' };
  estado = [i, ...estado];
  emitir();
  asegurarEscucha();
  return i;
}

/** Ingresa archivos. Varias fotos juntas (3 o más) se leen como las páginas de un mismo libro. */
export function ingerirArchivos(archivos: File[], opciones: { biblioteca?: string; fotosSueltas?: boolean } = {}): Ingesta[] {
  const imagenes = archivos.filter((f) => deducirTipo(f) === 'imagen');
  const resto = archivos.filter((f) => deducirTipo(f) !== 'imagen');
  const salida: Ingesta[] = [];
  if (imagenes.length >= 3 && !opciones.fotosSueltas) {
    const ordenadas = [...imagenes].sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true }));
    const total = ordenadas.reduce((s, f) => s + f.size, 0);
    const nombre = comunPrefijo(ordenadas.map((f) => f.name)) || `Libro fotografiado (${ordenadas.length} fotos)`;
    const i = nueva(nombre, 'fotos', total);
    i.unidades = ordenadas.length;
    void correr(i, ordenadas[0]!, opciones.biblioteca, ordenadas);
    salida.push(i);
  } else {
    resto.push(...imagenes);
  }
  for (const f of resto) {
    const i = nueva(nombreLegible(f.name), deducirTipo(f), f.size);
    void correr(i, f, opciones.biblioteca);
    salida.push(i);
  }
  return salida;
}

function comunPrefijo(nombres: string[]) {
  let p = nombres[0] ?? '';
  for (const n of nombres) while (!n.startsWith(p)) p = p.slice(0, -1);
  return p.replace(/[\s_\-.(]*\d*$/, '').trim();
}

export async function ingerirUrl(url: string, biblioteca?: string): Promise<Ingesta> {
  const tipo: TipoEntrada = /youtu\.?be|vimeo/.test(url) ? 'video' : /\.pdf($|\?)/i.test(url) ? 'pdf' : /\.(mp3|m4a|ogg|opus|wav)($|\?)|podcast|anchor\.fm|ivoox|spotify\.com\/episode/i.test(url) ? 'audio' : /\.(mp4|webm|mov)($|\?)/i.test(url) ? 'video' : 'web';
  const i = nueva(url.replace(/^https?:\/\/(www\.)?/, ''), tipo, 0);
  try {
    poner(i.id, { mensaje: 'Pidiendo la página…', etapa: 'procesando' });
    const r = await api().subidas.desdeUrl({ url, ...(biblioteca ? { bibliotecas: [biblioteca] } : {}), ...opcionesIngesta() });
    poner(i.id, { documento: r.documento, tarea: r.tarea, mensaje: 'Leyendo…' });
    void clienteConsultas.invalidateQueries({ queryKey: ['documentos'] });
  } catch (e) {
    poner(i.id, { etapa: 'error', error: mensajeDe(e) });
  }
  return i;
}

/** Lo que deja una ingesta lanzada desde la web (lo usan los lotes para apuntar cada elemento). */
export interface ResultadoIngesta { documento?: string; tarea?: string; duplicado?: string; error?: string }

/** Extras de una ingesta de lote: el modo del lote, los metadatos que ya se saben y la huella ya calculada. */
interface ExtrasIngesta { modo?: ModoIngesta; metadatos?: Record<string, unknown>; huella?: string; alCrear?: (documento: string) => void }

/**
 * Una ingesta para un lote: la misma tarjeta en la mesa de entrada y la misma
 * imprenta, pero devuelve el documento y la tarea en cuanto el servidor la acepta.
 */
export function ingerirParaLote(archivo: File, opciones: { biblioteca?: string } & ExtrasIngesta = {}): Promise<ResultadoIngesta> {
  const i = nueva(nombreLegible(archivo.name), deducirTipo(archivo), archivo.size);
  return correr(i, archivo, opciones.biblioteca, undefined, opciones);
}

async function correr(i: Ingesta, archivo: File, biblioteca?: string, fotos?: File[], extras: ExtrasIngesta = {}): Promise<ResultadoIngesta> {
  let conversion: Conversion | null = null;
  controles.set(i.id, { cancelar: () => conversion?.cancelar() });
  try {
    // La huella antes de subir: si el fichero ya está en la biblioteca, no se sube ni se lee otra vez.
    const huella = extras.huella ?? (!fotos && archivo.size < 512 * 1024 ** 2 ? await sha256Archivo(archivo) : undefined);
    const sub = await api().subidas.crear({
      nombre: archivo.name, mime: archivo.type || 'application/octet-stream', bytes: i.bytes, tipo: i.tipo, ...(huella ? { huella } : {}), ...(biblioteca ? { bibliotecas: [biblioteca] } : {}),
      ...(extras.metadatos ? { metadatos: extras.metadatos } : {}),
    });
    if (sub.duplicado) {
      poner(i.id, { etapa: 'duplicado', documento: sub.duplicado, avance: 1, mensaje: 'Ya estaba en tu biblioteca', fin: Date.now() });
      setTimeout(() => retirarIngesta(i.id), 5200);
      return { duplicado: sub.duplicado };
    }
    extras.alCrear?.(sub.documento);
    poner(i.id, { documento: sub.documento, mensaje: 'Subiendo y convirtiendo…', etapa: 'convirtiendo' });

    // 1. El original sube en paralelo a todo lo demás (por partes si es grande).
    const original = (async () => {
      if (fotos) return; // las fotos viajan como partes de la imprenta
      if (esSimulado()) {
        for (let k = 1; k <= 10; k++) { await new Promise((r) => setTimeout(r, 60)); poner(i.id, { subido: k / 10 }); }
        return;
      }
      await subirFichero(api(), sub.subida, sub.original, archivo, (env, tot) => poner(i.id, { subido: env / tot }));
    })();

    // 2. La imprenta del navegador, si sabe con este tipo.
    let paquete: PaqueteConversion | null = null;
    if (CONVIERTE_NAVEGADOR.has(i.tipo) && typeof Worker !== 'undefined') {
      conversion = convertirEnHilo(archivo, fotos);
      const partes = crearSubidorPartes(sub.subida);
      for await (const e of conversion.eventos) {
        if (e.tipo === 'no_disponible') break;
        if (e.tipo === 'inicio') poner(i.id, (x) => ({ unidades: e.unidades ?? x.unidades, ...(e.duracion ? { duracion: e.duracion } : {}), tipo: e.entrada, ...(e.metadatos.titulo?.trim() ? { nombre: e.metadatos.titulo.trim() } : {}) }));
        // En audio y vídeo `total` son segundos, no unidades: no se confunden.
        else if (e.tipo === 'progreso') poner(i.id, (x) => ({ preparadas: Math.max(x.preparadas, e.hechas), unidades: x.unidades ?? (e.fase === 'paginas' ? e.total : null), avance: Math.max(x.avance, 0.3 * (e.total ? e.hechas / e.total : 0)), mensaje: e.mensaje ?? x.mensaje }));
        else if (e.tipo === 'pagina_pdf') {
          // La capa de texto y el folio de /PageLabels: legible sin esperar al servidor.
          anotarPagina(sub.documento, e.pagina.fisica, { texto: e.pagina.cuerpo, impresa: e.pagina.etiqueta });
          if (e.pagina.texto.util) hito(i, 'legible');
        }
        else if (e.tipo === 'parte') {
          partes.anadir(e.parte, e.datos);
          if (e.parte.clase === 'pagina' && e.parte.unidad) {
            anotarPagina(sub.documento, e.parte.unidad, { imagenUrl: URL.createObjectURL(new Blob([e.datos as BlobPart], { type: e.parte.mime })) });
            hito(i, 'miniatura');
          }
          // En el vídeo, los fotogramas clave hacen de páginas que aparecen.
          if (e.parte.clase === 'miniatura' || (e.parte.clase === 'fotograma' && i.miniaturas.length < 400)) {
            const url = URL.createObjectURL(new Blob([e.datos as BlobPart], { type: e.parte.mime }));
            poner(i.id, (x) => ({ miniaturas: [...x.miniaturas, url] }));
            hito(i, 'miniatura');
          }
        } else if (e.tipo === 'fin') paquete = e.paquete;
      }
      if (paquete) {
        const json = new TextEncoder().encode(JSON.stringify(paquete));
        partes.anadir({ id: 'paquete.json', clase: 'recurso', mime: 'application/json', bytes: json.byteLength }, json);
        await partes.terminar();
      }
    }
    if (!paquete && esSimulado()) await simularMiniaturas(i);

    // Con paquete, la lectura empieza ya: el original sigue subiendo y el servidor lo espera al final.
    if (!paquete) await original;
    poner(i.id, { etapa: 'procesando', mensaje: 'Leyendo…', avance: 0.32 });
    const r = await api().subidas.ingestar(sub.subida, { ...(paquete ? { paquete: 'paquete.json' } : {}), ...(extras.modo ? (extras.modo === 'economico' ? { modo: 'economico' as const } : {}) : opcionesIngesta()) });
    poner(i.id, { tarea: r.tarea, documento: r.documento });
    void clienteConsultas.invalidateQueries({ queryKey: ['documentos'] });
    if (paquete) await original;
    return { documento: r.documento, tarea: r.tarea };
  } catch (e) {
    if (estado.find((x) => x.id === i.id)?.etapa === 'cancelada') return { error: 'Cancelado' };
    poner(i.id, { etapa: 'error', error: mensajeDe(e), fin: Date.now() });
    return { error: mensajeDe(e) };
  } finally {
    controles.delete(i.id);
  }
}

/** En la demostración no hay imprenta: dibuja miniaturas de papel para que se vea el gesto. */
async function simularMiniaturas(i: Ingesta) {
  if (!['pdf', 'pdf_escaneado', 'fotos', 'documento', 'epub', 'presentacion', 'imagen'].includes(i.tipo)) return;
  const n = Math.max(3, Math.min(14, Math.round(i.bytes / 80_000)));
  poner(i.id, { unidades: i.unidades ?? n });
  for (let k = 0; k < Math.min(n, 14); k++) {
    await new Promise((r) => setTimeout(r, 70));
    poner(i.id, (x) => ({ preparadas: k + 1, miniaturas: [...x.miniaturas, miniaturaDePapel(k)], avance: 0.3 * ((k + 1) / n) }));
  }
}

function miniaturaDePapel(k: number): string {
  const lineas = Array.from({ length: 9 }, (_, j) => `<rect x='10' y='${18 + j * 9}' width='${j === 8 ? 30 : 52 - ((k + j) % 3) * 6}' height='3' fill='%2322160f' opacity='.55'/>`).join('');
  return `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 72 100'><rect width='72' height='100' fill='%23faf7f0'/>${k === 0 ? "<rect x='10' y='10' width='40' height='5' fill='%23b8321c'/>" : ''}${lineas}<rect x='32' y='92' width='8' height='2' fill='%2322160f' opacity='.6'/></svg>`;
}

/**
 * Modo de ingesta elegido en Ajustes o en el menú «Añadir»: «rápido» (por
 * defecto) o «económico» (por lotes: listo en unas horas, la mitad de precio).
 */
export type ModoIngesta = 'rapido' | 'economico';
export function modoIngesta(): ModoIngesta {
  try { return localStorage.getItem('scholaris.modoIngesta') === 'economico' ? 'economico' : 'rapido'; } catch { return 'rapido'; }
}
export function ponerModoIngesta(m: ModoIngesta) {
  try { localStorage.setItem('scholaris.modoIngesta', m); } catch { /* sin almacenamiento */ }
}
/** Lo que viaja en `Ingestar` además del paquete. El campo `modo` lo define la plataforma. */
function opcionesIngesta(): Record<string, unknown> {
  return modoIngesta() === 'economico' ? { modo: 'economico' } : {};
}

export async function sha256Archivo(f: File): Promise<string | undefined> {
  try {
    const h = await crypto.subtle.digest('SHA-256', await f.arrayBuffer());
    return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch { return undefined; }
}

function mensajeDe(e: unknown): string {
  return e instanceof Error ? e.message : 'Algo falló al leer el archivo';
}

// ---------------------------------------------------------------------------
// Tiempo real: el avance del servidor
// ---------------------------------------------------------------------------

let escuchando = false;

function asegurarEscucha() {
  if (escuchando) return;
  escuchando = true;
  escucharTiempoReal(manejar);
}

function porTarea(tarea: string, documento?: string) {
  return estado.find((x) => x.tarea === tarea || (documento && x.documento === documento));
}

function manejar(e: EventoTiempoReal) {
  if (e.tipo === 'progreso') {
    const p = e.progreso;
    const i = porTarea(p.tarea, p.documento);
    if (!i) return;
    poner(i.id, (x) => ({
      etapa: p.fase === 'listo' ? 'listo' : 'procesando', fase: p.fase, tarea: p.tarea,
      avance: Math.max(x.avance, 0.32 + 0.68 * p.total), leidas: Math.max(x.leidas, p.unidadesListas ?? 0),
      // Unidades buscables mientras se lee (campo de la ingesta progresiva; opcional en el contrato).
      buscables: Math.max(x.buscables, p.unidadesBuscables ?? 0),
      mensaje: p.mensaje ?? x.mensaje, ...(p.error ? { etapa: 'error' as const, error: p.error } : {}),
    }));
  } else if (e.tipo === 'unidades') {
    const i = porTarea(e.tarea, e.documento);
    // `hasta` es un orden de la API (desde 0): leídas = hasta + 1.
    const buscable = (e as { buscables?: boolean }).buscables === true;
    if (i) { poner(i.id, (x) => ({ leidas: Math.max(x.leidas, e.hasta + 1), ...(buscable ? { buscables: Math.max(x.buscables, e.hasta + 1) } : {}) })); hito(i, 'legible'); if (buscable) hito(i, 'buscable'); }
    // Las páginas nuevas ya se pueden abrir en el lector.
    void clienteConsultas.invalidateQueries({ queryKey: ['unidades', e.documento] });
  } else if (e.tipo === 'fin') {
    const i = porTarea(e.tarea, e.documento);
    if (i) poner(i.id, { etapa: e.estado === 'listo' ? 'listo' : e.estado === 'cancelada' ? 'cancelada' : 'error', avance: 1, fin: Date.now(), ...(e.error ? { error: e.error } : {}) });
    if (i && e.estado === 'listo') hito(i, 'listo');
    void clienteConsultas.invalidateQueries({ queryKey: ['documentos'] });
    // Todo lo del documento se vuelve a pedir: lo que se cacheó mientras se leía estaba vacío.
    // Al conectar, el servidor repite el último «fin» de tareas ya cerradas: si no se estaba siguiendo
    // esa lectura y lo que hay en caché ya está listo, no hay nada nuevo que pedir.
    const enCache = e.documento ? clienteConsultas.getQueryData<{ estado?: string }>(['documento', e.documento]) : undefined;
    const repetido = !i && e.estado === 'listo' && enCache?.estado === 'listo';
    if (e.documento && !repetido) for (const k of ['documento', 'unidades', 'folios', 'secciones', 'figuras', 'original']) void clienteConsultas.invalidateQueries({ queryKey: [k, e.documento] });
    if (i && e.estado === 'listo') setTimeout(() => retirarIngesta(i.id), 5200);
  } else if (e.tipo === 'fase' && (e.fase === 'metadatos' || e.fase === 'estructura')) {
    // Título, autores e índice llegan antes del final: el lector y la biblioteca los recogen ya.
    void clienteConsultas.invalidateQueries({ queryKey: ['documento', e.documento] });
    void clienteConsultas.invalidateQueries({ queryKey: ['secciones', e.documento] });
    void clienteConsultas.invalidateQueries({ queryKey: ['documentos'] });
    const i = porTarea(e.tarea, e.documento);
    if (i && e.fase === 'metadatos') void clienteConsultas.fetchQuery({ queryKey: ['documento', e.documento], queryFn: () => api().documentos.obtener(e.documento) }).then((d) => d.metadatos.titulo && poner(i.id, { nombre: d.metadatos.titulo })).catch(() => undefined);
  } else if (e.tipo === 'alerta') {
    void clienteConsultas.invalidateQueries({ queryKey: ['alertas'] });
    void clienteConsultas.invalidateQueries({ queryKey: ['vigilantes'] });
  }
}

/** Al abrir la app: las tareas que ya estaban en marcha (otra pestaña, otro día) vuelven a la mesa. */
let recuperacion: Promise<void> | null = null;
export function recuperarTareas(otraVez = false): Promise<void> {
  if (otraVez) recuperacion = null;
  recuperacion ??= recuperarTareasAhora();
  return recuperacion;
}

async function recuperarTareasAhora() {
  asegurarEscucha();
  try {
    const [tareas, docs] = await Promise.all([api().tareas.listar(true), clienteConsultas.fetchQuery({ queryKey: ['documentos', {}], queryFn: () => api().documentos.listar({ limite: 500 }) })]);
    for (const t of tareas) {
      if (t.tipo !== 'ingesta' || estado.some((x) => x.tarea === t.id)) continue;
      const d = docs.elementos.find((x) => x.id === t.documento);
      const i: Ingesta = {
        id: `i-${t.id}`, nombre: d?.titulo ?? 'Documento', tipo: d?.tipo ?? 'pdf', bytes: d?.bytes ?? 0, documento: t.documento, tarea: t.id, etapa: 'procesando',
        fase: t.progreso?.fase, avance: 0.32 + 0.68 * (t.progreso?.total ?? 0), subido: 1, unidades: d?.unidades ?? null, preparadas: d?.unidades ?? 0,
        leidas: t.progreso?.unidadesListas ?? 0, buscables: 0, miniaturas: [], inicio: Date.parse(t.creada), mensaje: t.progreso?.mensaje ?? 'Leyendo…',
      };
      estado = [...estado, i];
    }
    emitir();
  } catch { /* sin tareas que recuperar */ }
}
